import { Grid, useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '../../../../Session';
import {
    FullSaleTransactionDataFragment,
    SaleTransactionStage,
    SalesTransactionActivityDataFragment,
    SettingValueUnit,
    UserType,
} from '../../../../api';
import Accordion from '../../../../components/Accordion';
import ContentWithLabel from '../../../../components/ContentWithLabel';
import useFormatCurrency from '../../../../utilities/useFormatCurrency';

export type TransactionTabProps = {
    saleTransaction: FullSaleTransactionDataFragment;
};

const TransactionTab = ({ saleTransaction }: TransactionTabProps) => {
    const { t } = useTranslation(['saleTransactionsPage', 'common']);
    const user = useCurrentUser();
    const { lastActivity } = saleTransaction;
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const useCardDesignForAccordion = !isSmall;
    const formatCurrency = useFormatCurrency();

    const getAuthorName = useCallback((activity: SalesTransactionActivityDataFragment) => {
        switch (activity.__typename) {
            case 'CreationActivity':
            case 'BiddingActivity':
            case 'SaleUpdateActivity':
            case 'SaleStageUpdateActivity':
            case 'SaleBroadcastActivity':
            case 'SaleCommentActivity':
                return activity.author.fullName;
            default:
                return '';
        }
    }, []);

    const { margin, handlingCharges } = useMemo(() => {
        if (saleTransaction.awardedBiddingEntry) {
            return {
                margin: saleTransaction.tradeInCalculation
                    ? formatCurrency(saleTransaction.tradeInCalculation.margin)
                    : t('common:blankValue'),
                handlingCharges: saleTransaction.tradeInCalculation
                    ? formatCurrency(saleTransaction.tradeInCalculation.handlingCharges)
                    : t('common:blankValue'),
            };
        }

        return {
            margin:
                saleTransaction.margin?.unit === SettingValueUnit.Amount
                    ? formatCurrency(saleTransaction.margin.value)
                    : t('saleTransactionsPage:transactionDetails.yetToBeAwarded'),
            handlingCharges:
                saleTransaction.handlingCharges?.unit === SettingValueUnit.Amount
                    ? formatCurrency(saleTransaction.handlingCharges.value)
                    : t('saleTransactionsPage:transactionDetails.yetToBeAwarded'),
        };
    }, [formatCurrency, saleTransaction, t]);

    // Define the fields to display in the transaction details section, along with the roles that can view them.
    const fieldsPerUserType = useMemo(
        () => [
            {
                key: 'awardedAmount',
                roles: [UserType.Admin, UserType.Root, UserType.SalesManager, UserType.Purchaser, UserType.Dealer],
                label: t('saleTransactionsPage:transactionDetails.awardedAmount'),
                render: () =>
                    saleTransaction.awardedBiddingEntry?.amount
                        ? formatCurrency(saleTransaction.awardedBiddingEntry?.amount)
                        : t('common:blankValue'),
            },
            {
                key: 'margin',
                roles: [UserType.Admin, UserType.Root, UserType.SalesManager, UserType.Purchaser],
                label: t('saleTransactionsPage:transactionDetails.margin'),
                render: () => margin,
            },
            {
                key: 'handlingCharges',
                roles: [UserType.Admin, UserType.Root, UserType.SalesManager, UserType.Purchaser, UserType.Dealer],
                label: t('saleTransactionsPage:transactionDetails.handlingCharges'),
                render: () => handlingCharges,
            },
            {
                key: 'tradeInAmount',
                roles: [
                    UserType.Admin,
                    UserType.Root,
                    UserType.SalesManager,
                    UserType.Purchaser,
                    UserType.SaleConsultant,
                ],
                label: t('saleTransactionsPage:transactionDetails.tradeInAmount'),
                render: () =>
                    saleTransaction.tradeInCalculation?.tradeInAmount != null
                        ? formatCurrency(saleTransaction.tradeInCalculation.tradeInAmount)
                        : t('common:blankValue'),
            },
            {
                key: 'totalPaymentAmount',
                roles: [UserType.Dealer],
                label: t('saleTransactionsPage:transactionDetails.totalPaymentAmount'),
                render: () =>
                    saleTransaction.tradeInCalculation?.totalPaymentAmount != null
                        ? formatCurrency(saleTransaction.tradeInCalculation.totalPaymentAmount)
                        : t('common:blankValue'),
            },
        ],
        [
            handlingCharges,
            margin,
            formatCurrency,
            saleTransaction.awardedBiddingEntry?.amount,
            saleTransaction.tradeInCalculation?.totalPaymentAmount,
            saleTransaction.tradeInCalculation?.tradeInAmount,
            t,
        ]
    );

    return (
        <Grid spacing={2} container>
            <Grid xs={12} item>
                <Accordion
                    title={t('saleTransactionsPage:accordionTitle.transactionDetail')}
                    useCardDesign={useCardDesignForAccordion}
                    defaultExpanded
                >
                    <Grid spacing={1} container>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={saleTransaction.identifier}
                                label={t('saleTransactionsPage:transactionDetails.transactionNo')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={
                                    saleTransaction.stage === SaleTransactionStage.ForHandover
                                        ? 'Awarded'
                                        : saleTransaction.stage
                                }
                                label={t('saleTransactionsPage:transactionDetails.transactionStatus')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={t('common:formats.shortDate', { date: new Date(saleTransaction.createdAt) })}
                                label={t('saleTransactionsPage:transactionDetails.createdOn')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={saleTransaction.createdBy.fullName}
                                label={t('saleTransactionsPage:transactionDetails.createdBy')}
                            />
                        </Grid>
                        {fieldsPerUserType
                            .filter(field => field.roles.includes(user.type))
                            .map(field => (
                                <Grid key={field.key} xs={12} item>
                                    <ContentWithLabel content={field.render()} label={field.label} />
                                </Grid>
                            ))}
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={saleTransaction.awardedBiddingEntry?.dealer.fullName || t('common:blankValue')}
                                label={t('saleTransactionsPage:transactionDetails.awardee')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={saleTransaction.salesConsultant?.fullName || t('common:blankValue')}
                                label={t('saleTransactionsPage:transactionDetails.salesConsultant')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={t('common:formats.shortDate', { date: new Date(saleTransaction.updatedAt) })}
                                label={t('saleTransactionsPage:transactionDetails.lastUpdatedOn')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={t('common:blankValue')}
                                label={t('saleTransactionsPage:transactionDetails.closedOn')}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <ContentWithLabel
                                content={
                                    saleTransaction.internalRemarks
                                        ? saleTransaction.internalRemarks
                                        : t('common:blankValue')
                                }
                                display="block"
                                label={t('saleTransactionsPage:transactionDetails.internalRemarks')}
                            />
                        </Grid>
                    </Grid>
                </Accordion>
            </Grid>

            <Grid xs={12} item>
                <Accordion
                    title={t('saleTransactionsPage:accordionTitle.activityLog')}
                    useCardDesign={useCardDesignForAccordion}
                    defaultExpanded
                >
                    {lastActivity && (
                        <Grid spacing={1} container>
                            <Grid xs={12} item>
                                <ContentWithLabel
                                    content={lastActivity.type}
                                    label={t('saleTransactionsPage:activity.action')}
                                />
                            </Grid>
                            <Grid xs={12} item>
                                <ContentWithLabel
                                    content={getAuthorName(lastActivity)}
                                    label={t('saleTransactionsPage:activity.updatedBy')}
                                />
                            </Grid>
                            <Grid xs={12} item>
                                <ContentWithLabel
                                    content={t('common:formats.shortDate', {
                                        date: new Date(lastActivity.date),
                                    })}
                                    label={t('saleTransactionsPage:activity.updatedOn')}
                                />
                            </Grid>
                        </Grid>
                    )}
                </Accordion>
            </Grid>
        </Grid>
    );
};

export default TransactionTab;
